/* ==========================================================================
   theme.js — Custom global utilities and Web Components
   Classic deferred script. No ES module exports.
   Dawn globals already available: subscribe, publish, debounce, trapFocus,
   removeTrapFocus, getFocusableElements, onKeyUpEscape, pauseAllMedia.
   ========================================================================== */

// --- Custom event name constants ---
const CUSTOM_EVENTS = {
  toastShow:      'theme:toast:show',
  drawerOpen:     'theme:drawer:open',
  drawerClose:    'theme:drawer:close',
  cartItemAdded:  'theme:cart:item-added',
  cartDrawerOpen: 'theme:cart-drawer:open',
  overlayShow:    'theme:overlay:show',
  overlayHide:    'theme:overlay:hide',
};

/**
 * Fetch JSON from a URL. Throws on non-OK responses.
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<any>}
 */
async function fetchJSON(url, options) {
  var opts = Object.assign(
    { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
    options
  );
  var response = await fetch(url, opts);
  if (!response.ok) {
    throw new Error('fetchJSON ' + response.status + ' ' + response.statusText + ' — ' + url);
  }
  return response.json();
}

/**
 * POST JSON body to a Shopify cart endpoint.
 * @param {string} endpoint e.g. '/cart/add.js'
 * @param {object} body
 * @returns {Promise<any>}
 */
function cartFetch(endpoint, body) {
  return fetchJSON(endpoint, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Dispatch a toast notification from anywhere on the page.
 * @param {string} message
 * @param {{ title?: string, type?: 'success'|'error'|'warning'|'info', duration?: number }} [options]
 */
function showToast(message, options) {
  var detail = Object.assign({ message: message }, options);
  document.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.toastShow, { bubbles: true, detail: detail }));
}

/**
 * Open a ThemeDrawer by its id attribute.
 * @param {string} drawerId
 */
function openDrawer(drawerId) {
  document.dispatchEvent(
    new CustomEvent(CUSTOM_EVENTS.drawerOpen, { bubbles: true, detail: { id: drawerId } })
  );
}

/**
 * Close a ThemeDrawer (omit id to close any open drawer).
 * @param {string} [drawerId]
 */
function closeDrawer(drawerId) {
  document.dispatchEvent(
    new CustomEvent(CUSTOM_EVENTS.drawerClose, { bubbles: true, detail: { id: drawerId } })
  );
}

// ==========================================================================
// Toast Web Component
// ==========================================================================
class ThemeToast extends HTMLElement {
  connectedCallback() {
    this._handleShow = this._onShow.bind(this);
    document.addEventListener(CUSTOM_EVENTS.toastShow, this._handleShow);
  }

  disconnectedCallback() {
    document.removeEventListener(CUSTOM_EVENTS.toastShow, this._handleShow);
  }

  _onShow(event) {
    var detail   = event.detail || {};
    var message  = detail.message  || '';
    var title    = detail.title    || '';
    var type     = detail.type     || 'default';
    var duration = detail.duration != null ? detail.duration : 4000;

    var toast = this._createToast(message, title, type);
    this.appendChild(toast);

    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        toast.classList.add('is-visible');
      });
    });

    var self = this;
    var dismiss = function() { self._dismiss(toast); };
    if (duration > 0) setTimeout(dismiss, duration);

    var btn = toast.querySelector('.toast__dismiss');
    if (btn) btn.addEventListener('click', dismiss);
  }

  _createToast(message, title, type) {
    var el = document.createElement('div');
    var cls = 'toast';
    if (type && type !== 'default') cls += ' toast--' + type;
    el.className = cls;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    var html = '<div class="toast__content">';
    if (title)   html += '<p class="toast__title">' + this._esc(title) + '</p>';
    if (message) html += '<p class="toast__message">' + this._esc(message) + '</p>';
    html += '</div>';
    html += '<button class="toast__dismiss" aria-label="Dismiss" type="button">';
    html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    html += '</button>';

    el.innerHTML = html;
    return el;
  }

  _dismiss(toast) {
    toast.classList.remove('is-visible');
    toast.addEventListener('transitionend', function() { toast.remove(); }, { once: true });
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('theme-toast')) {
  customElements.define('theme-toast', ThemeToast);
}

// ==========================================================================
// Drawer Web Component
// ==========================================================================
class ThemeDrawer extends HTMLElement {
  connectedCallback() {
    this._overlay       = document.querySelector('.drawer-overlay');
    this._boundKeydown  = this._onKeydown.bind(this);
    this._boundOverlay  = this.close.bind(this);

    var self = this;
    document.addEventListener(CUSTOM_EVENTS.drawerOpen,  function(e) {
      if (e.detail && e.detail.id === self.id) self.open();
    });
    document.addEventListener(CUSTOM_EVENTS.drawerClose, function(e) {
      if (!e.detail || !e.detail.id || e.detail.id === self.id) self.close();
    });

    var closeBtn = this.querySelector('.drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', this._boundOverlay);
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    if (this._overlay) this._overlay.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this._boundKeydown);
    if (this._overlay) this._overlay.addEventListener('click', this._boundOverlay);

    var first = this.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (first) requestAnimationFrame(function() { first.focus(); });
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    if (this._overlay) this._overlay.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this._boundKeydown);
    if (this._overlay) this._overlay.removeEventListener('click', this._boundOverlay);
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}
